// @ts-check

import { useQuery, useMutation } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import { isEmpty } from "lodash";
import {
  getOrgConfigForWorkspace as getOrgConfigForWorkspaceQuery,
  updateOrgConfigForWorkspace as updateOrgConfigForWorkspaceMutation,
} from "src/queries/orgConfig.graphql";
import { omitTypename } from "src/util/graphql";

/**
 * @typedef {import("@apollo/client").ApolloError} ApolloError
 */

/**
 * Hook for managing org config for workspace.
 * @returns {{
 *  loading: boolean,
 *  config: org_config.workspace.Config,
 *  createdBy: string,
 *  createdAt: import("moment").Moment,
 *  update: function(org_config.workspace.Config): Promise<boolean>
 *  updating: boolean,
 * }}
 */
export const useOrgConfigForWorkspace = () => {
  const { data, loading } = useQuery(getOrgConfigForWorkspaceQuery, {
    fetchPolicy: "cache-first", // all config updates will update cache so can avoid spamming the network
  });

  const [updateConfig, { loading: updating }] = useMutation(
    updateOrgConfigForWorkspaceMutation
  );

  const config = useMemo(() => omitTypename(data?.config), [data]);

  const update = useCallback(
    /**
     * Partial update
     * @param {org_config.workspace.Config} newConfig
     * @returns {Promise<boolean>}
     */
    async (newConfig) => {
      if (loading) {
        return false;
      }

      const { errors } = await updateConfig({
        variables: { input: { config: newConfig } },
        update: (cache, { data: updateData, errors: updateErrors }) => {
          if (!isEmpty(updateErrors) || !updateData?.config) {
            return;
          }

          const existingData = cache.readQuery({
            query: getOrgConfigForWorkspaceQuery,
          });

          if (existingData) {
            cache.writeQuery({
              query: getOrgConfigForWorkspaceQuery,
              data: {
                config: updateData.config,
              },
            });
          }
        },
      });

      if (!isEmpty(errors)) {
        return false;
      }

      return true;
    },
    [updateConfig, loading]
  );

  return {
    loading,
    config: config?.config,
    createdBy: config?.createdBy,
    createdAt: config?.createdAt,
    update,
    updating,
  };
};
